import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Select, { components } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import SectionHeader from "components/SectionPillPropsIcons";
import { fetchMarketplaceProfilesSelect2 } from "api/postTypeApiCalls";
import { showError, showSuccess } from "toast/toast";
import { getAuthCookie } from "../../utils/authCookies";
import {
  createClientProject,
  fetchClientProjectById,
  getDesignRequestBase,
  isAgentDesignRequestRole,
  isBrokerageDesignRequestRole,
  isDraftLikeProjectStatus,
  resolveVendorOrganizationName,
  updateClientProject,
} from "components/designRequestApi";
import PONumber from "components/PONumber";
import "pages/signage/projectandquote-addedit.css";

const PAGE_SIZE = 10;

const DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
  </components.DropdownIndicator>
);

const customSelectStyles = {
  dropdownIndicator: (provided: any) => ({
    ...provided,
    display: "flex !important",
    opacity: 1,
    visibility: "visible",
  }),
  indicatorSeparator: (provided: any) => ({ ...provided, display: "none" }),
  menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }),
};

const selectMenuPortal = typeof document !== "undefined" ? document.body : undefined;

type PartyOption = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  organizationId?: string;
};

function vendorEmailFromSelect2(row: any): string {
  return String(row?.email ?? row?.contact_info?.email ?? "").trim();
}

function VendorNameEmailLabel({ option }: { option: PartyOption }) {
  const emailText = String(option.email || "").trim();
  return (
    <span style={{ lineHeight: 1.3 }}>
      <span className="d-block">{option.label}</span>
      {emailText ? <small className="text-muted d-block">{emailText}</small> : null}
    </span>
  );
}

type WorkflowItem = {
  label: string;
  detail: string;
  iconClass: string;
  tone: "blue" | "purple" | "green" | "orange" | "mint";
};

const createWorkflowItems: WorkflowItem[] = [
  { label: "Request created", detail: "New design request sent to vendor", iconClass: "ri-add-line", tone: "blue" },
  { label: "Design discussions", detail: "You and the vendor collaborate on requirements", iconClass: "ri-message-2-line", tone: "purple" },
  { label: "Design approved", detail: "Final artwork is confirmed", iconClass: "ri-thumb-up-line", tone: "green" },
  { label: "Quoted", detail: "Vendor prepares a quote from the approved design", iconClass: "ri-money-dollar-circle-line", tone: "orange" },
];

function isAllowedArtworkFile(file: File): boolean {
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  if (["png", "jpg", "jpeg", "pdf", "ai"].includes(ext)) return true;
  const t = file.type || "";
  return t === "image/png" || t === "image/jpeg" || t === "application/pdf" || t === "application/postscript";
}

const buyerOptions = [
  { value: "teammember", label: "Team Member" },
  { value: "agent", label: "Agent" },
];

const DesignRequestAddEdit: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams<{ projectId?: string }>();
  const listPath = getDesignRequestBase(location.pathname);
  const isEdit = Boolean(projectId);
  const isAgent = isAgentDesignRequestRole(location.pathname);
  const isBrokerage = isBrokerageDesignRequestRole(location.pathname);
  const isTeammember = location.pathname.includes("/teammember");

  const token = getAuthCookie("sessionToken") || "";
  const userId = getAuthCookie("userId") || "";
  const licenseType = getAuthCookie("licenseType") || "";

  const [userInfo, setUserInfo] = useState<any>(null);
  const [parentBrokerageId, setParentBrokerageId] = useState<string>("");
  const [parentBrokerageName, setParentBrokerageName] = useState<string>("");
  const [loadingProject, setLoadingProject] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [projectStatus, setProjectStatus] = useState("");

  const [selectedVendor, setSelectedVendor] = useState<PartyOption | null>(null);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [artworkFiles, setArtworkFiles] = useState<File[]>([]);
  const [artworkDragActive, setArtworkDragActive] = useState(false);

  const [userType, setUserType] = useState<"teammember" | "agent" | "">("");
  const [selectedOffice, setSelectedOffice] = useState<PartyOption | null>(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState<PartyOption | null>(null);
  const [selectedOfficeAgent, setSelectedOfficeAgent] = useState<PartyOption | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<PartyOption | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");

  const brokerageId = String(
    (isTeammember
      ? parentBrokerageId ||
        userInfo?.created_by?.id ||
        userInfo?.firmDetails?.id ||
        userInfo?.firm_id
      : userId) ||
      userId ||
      ""
  ).trim();

  useEffect(() => {
    if (!token || !userId) return;
    fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        setUserInfo(data);
        if (isTeammember) {
          const fromUser = String(
            data.created_by?.id || data.firmDetails?.id || data.firm_id || ""
          ).trim();
          if (fromUser) setParentBrokerageId((prev) => prev || fromUser);
          const fromName = String(data.firmDetails?.companyName || data.created_by?.name || "").trim();
          if (fromName) setParentBrokerageName((prev) => prev || fromName);
        }
        if (isAgent) {
          setFirstName(data.firstName || "");
          setLastName(data.lastName || "");
          setUserEmail(data.email || "");
          setUserPhone(data.phone1 || data.phone2 || "");
        }
      })
      .catch(() => undefined);
  }, [token, userId, isAgent, isTeammember]);

  useEffect(() => {
    if (!isTeammember || userType) return;
    if (brokerageId) setUserType("teammember");
  }, [isTeammember, brokerageId, userType]);

  useEffect(() => {
    if (!isTeammember || !token) return;
    fetch(`${process.env.REACT_APP_API_URL}/parent-hierarchy`, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const brokerageFirm = data?.parent_chain?.find((p: any) => p.license_type === "BROKERAGE_FIRM");
        if (brokerageFirm) {
          setParentBrokerageId(brokerageFirm.id || brokerageFirm._id || "");
          setParentBrokerageName(brokerageFirm.name || "");
        }
      })
      .catch(() => undefined);
  }, [isTeammember, token]);

  const canEditCustomer = !isEdit || isDraftLikeProjectStatus(projectStatus);

  useEffect(() => {
    if (!isEdit || !projectId || !token) return;
    let cancelled = false;
    (async () => {
      setLoadingProject(true);
      try {
        const detail = await fetchClientProjectById(token, projectId);
        if (cancelled) return;
        setProjectStatus(detail.status || "");
        setTitle(detail.title || "");
        setMessage(detail.notes || "");
        setPoNumber(String((detail as any)?.po_number ?? (detail as any)?.poNumber ?? "").trim());
        setFirstName(detail.first_name || (detail.customer_name || "").split(" ")[0] || "");
        setLastName(detail.last_name || (detail.customer_name || "").split(" ").slice(1).join(" ") || "");
        setUserEmail(detail.customer_email || "");
        setUserPhone(detail.customer_phone || "");
        if (detail.vendor_org_id) {
          const vendorLabel =
            detail.designer_name ||
            (await resolveVendorOrganizationName(token, detail.vendor_org_id)) ||
            "Selected vendor";
          if (cancelled) return;
          setSelectedVendor({
            value: detail.vendor_org_id,
            label: vendorLabel,
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            organizationId: detail.vendor_org_id,
          });
        }
        if (detail.office?._id) {
          setUserType("teammember");
          setSelectedOffice({
            value: detail.office._id,
            label: detail.office.officeName || "Office",
            firstName: detail.office.officeName || "",
            lastName: "",
            email: "",
            phone: "",
          });
        } else if (detail.agent_membership_id) {
          setUserType("agent");
        }
        if (detail.team_member_membership_id) {
          setSelectedTeamMember({
            value: detail.team_member_membership_id,
            label: detail.team_member_name || "Team Member",
            firstName: detail.first_name || "",
            lastName: detail.last_name || "",
            email: detail.customer_email || "",
            phone: detail.customer_phone || "",
          });
        }
        if (detail.agent_membership_id) {
          const agentOpt: PartyOption = {
            value: detail.agent_membership_id,
            label: detail.agent_name || "Agent",
            firstName: detail.first_name || "",
            lastName: detail.last_name || "",
            email: detail.customer_email || "",
            phone: detail.customer_phone || "",
          };
          if (detail.office?._id) setSelectedOfficeAgent(agentOpt);
          else setSelectedAgent(agentOpt);
        }
      } catch (e) {
        showError(e instanceof Error ? e.message : "Failed to load design request");
      } finally {
        if (!cancelled) setLoadingProject(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, projectId, token]);

  const loadVendorOptions: LoadOptions<PartyOption, any, { page: number }> = useCallback(
    async (search, _, additional) => {
      if (!token) return { options: [], hasMore: false };
      const page = additional?.page ?? 1;
      try {
        const { results, pagination } = await fetchMarketplaceProfilesSelect2(token, {
          search: search || "",
          page,
          limit: 10,
          category: "signage",
        });
        const options: PartyOption[] = results.map((r: any) => {
          const organizationId = String(r.organization_id || r.organizationId || r.vendor_id || r.id || "").trim();
          return {
            value: organizationId,
            label: r.text || "Vendor",
            firstName: "",
            lastName: "",
            email: vendorEmailFromSelect2(r),
            phone: "",
            organizationId,
          };
        }).filter((o) => o.value);
        return { options, hasMore: pagination?.more ?? false, additional: { page: page + 1 } };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [token]
  );

  const loadOfficeOptions: LoadOptions<PartyOption, any, { page: number }> = useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      if (!brokerageId || !token) return { options: [], hasMore: false, additional: { page } };
      const response = await fetch(`${process.env.REACT_APP_API_URL}/office/select/${brokerageId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ page, limit: PAGE_SIZE, search: (inputValue || "").trim() }),
      });
      if (!response.ok) return { options: [], hasMore: false, additional: { page } };
      const json = await response.json();
      const list = json?.results || [];
      const options: PartyOption[] = list.map((office: any) => ({
        value: office.id || office._id,
        label: office.officeName || "---",
        firstName: office.officeName || "",
        lastName: "",
        email: "",
        phone: "",
      }));
      return { options, hasMore: list.length === PAGE_SIZE, additional: { page: page + 1 } };
    },
    [brokerageId, token]
  );

  const loadTeamMemberOptions: LoadOptions<PartyOption, any, { page: number }> = useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      const officeId = selectedOffice?.value;
      if (!officeId || !token) return { options: [], hasMore: false, additional: { page } };
      const response = await fetch(`${process.env.REACT_APP_API_URL}/office/${officeId}/team-members/select`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ page, limit: PAGE_SIZE, search: (inputValue || "").trim() }),
      });
      if (!response.ok) return { options: [], hasMore: false, additional: { page } };
      const json = await response.json();
      const results = Array.isArray(json) ? json : json.results || [];
      const options: PartyOption[] = results.map((row: any) => ({
        value: String(row?.id ?? row?._id ?? ""),
        label: `${row?.firstName || ""} ${row?.lastName || ""}`.trim() || row?.text || "(no name)",
        firstName: row?.firstName || "",
        lastName: row?.lastName || "",
        email: row?.email || "",
        phone: row?.phone1 || "",
      }));
      return { options, hasMore: results.length === PAGE_SIZE, additional: { page: page + 1 } };
    },
    [selectedOffice?.value, token]
  );

  const loadOfficeAgentOptions: LoadOptions<PartyOption, any, { page: number }> = useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      const officeId = selectedOffice?.value;
      if (!officeId || !token) return { options: [], hasMore: false, additional: { page } };
      const response = await fetch(`${process.env.REACT_APP_API_URL}/agent/select/${officeId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ page, limit: PAGE_SIZE, search: (inputValue || "").trim() }),
      });
      if (!response.ok) return { options: [], hasMore: false, additional: { page } };
      const json = await response.json();
      const results = json?.results || [];
      const options: PartyOption[] = results.map((row: any) => ({
        value: String(row?.id ?? row?._id ?? ""),
        label: `${row?.firstName || ""} ${row?.lastName || ""}`.trim() || row?.text || "(no name)",
        firstName: row?.firstName || "",
        lastName: row?.lastName || "",
        email: row?.email || "",
        phone: row?.phone1 || "",
      }));
      return { options, hasMore: results.length === PAGE_SIZE, additional: { page: page + 1 } };
    },
    [selectedOffice?.value, token]
  );

  const loadAgentOptions: LoadOptions<PartyOption, any, { page: number }> = useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      if (!brokerageId || !token) return { options: [], hasMore: false, additional: { page } };
      const params = new URLSearchParams({
        licenseType: "BROKERAGE_AGENT",
        brokerageFirmID: brokerageId,
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      const search = (inputValue || "").trim();
      if (search) params.set("search", search);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users-search?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      if (!response.ok) return { options: [], hasMore: false, additional: { page } };
      const responseData = await response.json();
      const results = Array.isArray(responseData) ? responseData : responseData.results || [];
      const options: PartyOption[] = results.map((row: any) => ({
        value: String(row?.id ?? row?._id ?? ""),
        label: `${row?.firstName || ""} ${row?.lastName || ""}`.trim() || row?.text || "(no name)",
        firstName: row?.firstName || "",
        lastName: row?.lastName || "",
        email: row?.email || "",
        phone: row?.phone1 || "",
      }));
      return { options, hasMore: results.length === PAGE_SIZE, additional: { page: page + 1 } };
    },
    [brokerageId, token]
  );

  const resetPartyContact = () => {
    setFirstName("");
    setLastName("");
    setUserEmail("");
    setUserPhone("");
  };

  const applyPerson = (opt: PartyOption | null) => {
    setFirstName(opt?.firstName || "");
    setLastName(opt?.lastName || "");
    setUserEmail(opt?.email || "");
    setUserPhone(opt?.phone || "");
  };

  const mergeArtworkFiles = (incoming: FileList | File[]) => {
    const add: File[] = [];
    Array.from(incoming).forEach((f) => {
      if (!isAllowedArtworkFile(f)) {
        showError(`${f.name} is not an allowed file type (PNG, JPG, PDF, AI).`);
        return;
      }
      add.push(f);
    });
    if (add.length) setArtworkFiles((prev) => [...prev, ...add]);
  };

  const omitEmpty = (obj: Record<string, unknown>) => {
    const out: Record<string, unknown> = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (v == null) return;
      if (typeof v === "string" && !v.trim()) return;
      out[k] = v;
    });
    return out;
  };

  const buildPartyPayload = () => {
    const customerName = `${firstName} ${lastName}`.trim();
    const brokerageOrgId = String(
      userInfo?.firmDetails?.id ||
        userInfo?.organization_id ||
        userInfo?.firm_id ||
        parentBrokerageId ||
        (isBrokerage && !isTeammember ? userId : "")
    ).trim();
    const payload: Record<string, unknown> = {
      title: title.trim(),
      notes: message.trim() || undefined,
      po_number: poNumber.trim() || undefined,
      first_name: firstName.trim() || undefined,
      last_name: lastName.trim() || undefined,
      customer_name: customerName || undefined,
      customer_email: userEmail.trim() || undefined,
      customer_phone: userPhone.trim() || undefined,
      license_type: userInfo?.licenseType || licenseType || undefined,
      brokerage_firm_organization_id: brokerageOrgId || undefined,
    };

    if (isBrokerage && selectedOffice?.value) {
      payload.office = { _id: selectedOffice.value, officeName: selectedOffice.label || "Office" };
    }
    if (isBrokerage && userType === "agent" && selectedAgent?.value) {
      payload.agent_membership_id = selectedAgent.value;
      payload.agent_name = selectedAgent.label;
    }
    if (isBrokerage && userType === "teammember" && selectedTeamMember?.value) {
      payload.team_member_membership_id = selectedTeamMember.value;
      payload.team_member_name = selectedTeamMember.label;
    }
    if (isBrokerage && userType === "teammember" && selectedOfficeAgent?.value) {
      payload.agent_membership_id = selectedOfficeAgent.value;
      payload.agent_name = selectedOfficeAgent.label;
    }
    if (isAgent) {
      payload.agent_membership_id = userId;
      payload.agent_name = `${firstName} ${lastName}`.trim() || userInfo?.firstName;
    }
    return omitEmpty(payload);
  };

  const handleSubmit = async () => {
    if (isSaving) return;
    const vendorOrgId = selectedVendor?.organizationId || selectedVendor?.value || "";
    if (!vendorOrgId) {
      showError("Select a vendor to continue");
      return;
    }
    if (!title.trim()) {
      showError("Enter a design title");
      return;
    }
    if (!isEdit && artworkFiles.length === 0) {
      showError("Add at least one artwork file");
      return;
    }
    if (!userEmail.trim()) {
      showError("Email is required");
      return;
    }
    if (isBrokerage) {
      if (!userType) {
        showError("Select a buyer type");
        return;
      }
      if (userType === "agent" && !selectedAgent?.value) {
        showError("Select an agent to continue");
        return;
      }
      if (userType === "teammember" && !selectedOffice?.value) {
        showError("Select an office to continue");
        return;
      }
      if (userType === "teammember" && !selectedTeamMember?.value) {
        showError("Select a team member to continue");
        return;
      }
    }

    setIsSaving(true);
    try {
      const partyPayload = buildPartyPayload();
      if (isEdit && projectId) {
        await updateClientProject(token, projectId, partyPayload);
        showSuccess("Design request updated");
        navigate(`${listPath}/view/${encodeURIComponent(projectId)}`);
      } else {
        const created = await createClientProject(token, { ...partyPayload, vendor_org_id: vendorOrgId }, artworkFiles);
        showSuccess("Design request created");
        navigate(`${listPath}/view/${encodeURIComponent(created.projectId)}`);
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to save design request");
    } finally {
      setIsSaving(false);
    }
  };

  const workflowUi = useMemo(() => createWorkflowItems, []);

  if (loadingProject) {
    return <div className="pq-create-page p-5">Loading design request…</div>;
  }

  return (
    <div className="pq-create-page">
      <div className="pq-create-header">
        <div>
          <h1 className="pq-create-title">{isEdit ? "Edit Design Request" : "Create a Design Request"}</h1>
          <p className="pq-create-subtitle">
            {isEdit ? "Update request details and requirements" : "Start a new design conversation with a vendor"}
          </p>
        </div>
        <button className="pq-top-cancel-btn" type="button" onClick={() => navigate(listPath)}>
          Cancel
        </button>
      </div>

      <div className="pq-create-layout">
        <div className="pq-create-left">
          <div className="pq-create-card">
            <div className="pq-create-card-body">
              <div className="pq-form-group">
                <label className="pq-form-label">
                  Select Vendor <span className="text-danger">*</span>
                </label>
                <AsyncPaginate
                  key={`vendor-${selectedVendor?.value || "new"}-${selectedVendor?.label || ""}`}
                  classNamePrefix="rs"
                  placeholder="Search vendors…"
                  value={selectedVendor}
                  loadOptions={loadVendorOptions}
                  onChange={(opt) => setSelectedVendor(opt as PartyOption | null)}
                  additional={{ page: 1 }}
                  defaultOptions
                  debounceTimeout={300}
                  isClearable={!isEdit}
                  isDisabled={isEdit}
                  formatOptionLabel={(option: PartyOption) => <VendorNameEmailLabel option={option} />}
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                />
              </div>
            </div>
          </div>

          <div className="row col-lg-12 p-3 card-body shadow-lg pb-4 mb-4 ms-0">
            <div className="mb-3">
              <SectionHeader title="Customer Information" icon="ri-user-3-line" color="#155dfc" size="md" divider />
              {isEdit && !canEditCustomer ? (
                <div className="text-muted small mt-1">Customer information can be changed only while the request is in draft / in progress.</div>
              ) : null}
            </div>

            {isAgent ? (
              <>
                <div className="col-lg-3 mb-3">
                  <label className="form-label table-headers">First Name <span className="text-danger">*</span></label>
                  <input className="form-control table-cells" value={firstName} readOnly />
                </div>
                <div className="col-lg-3 mb-3">
                  <label className="form-label table-headers">Last Name <span className="text-danger">*</span></label>
                  <input className="form-control table-cells" value={lastName} readOnly />
                </div>
                <div className="col-lg-3 mb-3">
                  <label className="form-label table-headers">Email <span className="text-danger">*</span></label>
                  <input className="form-control table-cells" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} readOnly={!canEditCustomer} />
                </div>
                <div className="col-lg-3 mb-3">
                  <label className="form-label table-headers">Phone <span className="text-danger">*</span></label>
                  <input className="form-control table-cells" value={userPhone} onChange={(e) => setUserPhone(e.target.value)} readOnly={!canEditCustomer} />
                </div>
              </>
            ) : (
              <>
                <div className="col-lg-3 mb-3">
                  <label className="form-label table-cells">Buyer Type <span className="text-danger">*</span></label>
                  <Select
                    options={buyerOptions}
                    value={buyerOptions.find((o) => o.value === userType) || null}
                    isDisabled={!canEditCustomer}
                    onChange={(opt) => {
                      setSelectedTeamMember(null);
                      setSelectedOffice(null);
                      setSelectedOfficeAgent(null);
                      setSelectedAgent(null);
                      resetPartyContact();
                      setUserType((opt?.value as "teammember" | "agent") || "");
                    }}
                    components={{ DropdownIndicator }}
                    styles={customSelectStyles}
                    classNamePrefix="rs"
                    menuPortalTarget={selectMenuPortal}
                  />
                </div>

                {userType === "teammember" && brokerageId ? (
                  <>
                    <div className="col-lg-3 mb-3">
                      <label className="table-cells">Select Office <span className="text-danger">*</span></label>
                      <AsyncPaginate
                        key={`office-${brokerageId}-${userType}`}
                        classNamePrefix="rs"
                        placeholder="Select office…"
                        value={selectedOffice}
                        loadOptions={loadOfficeOptions}
                        isDisabled={!canEditCustomer}
                        cacheUniqs={[brokerageId, userType]}
                        onChange={(selected) => {
                          setSelectedOffice(selected as PartyOption | null);
                          setSelectedTeamMember(null);
                          setSelectedOfficeAgent(null);
                          resetPartyContact();
                        }}
                        additional={{ page: 1 }}
                        defaultOptions
                        debounceTimeout={300}
                        isClearable
                        components={{ DropdownIndicator }}
                        styles={customSelectStyles}
                        menuPortalTarget={selectMenuPortal}
                      />
                    </div>
                    {selectedOffice ? (
                      <div className="col-lg-3 mb-3">
                        <label className="table-cells">Select Team Member <span className="text-danger">*</span></label>
                        <AsyncPaginate
                          key={`office-${selectedOffice.value}`}
                          classNamePrefix="rs"
                          placeholder="Select team member…"
                          value={selectedTeamMember}
                          loadOptions={loadTeamMemberOptions}
                          isDisabled={!canEditCustomer}
                          cacheUniqs={[selectedOffice.value]}
                          onChange={(selected) => {
                            setSelectedTeamMember(selected as PartyOption | null);
                            setSelectedOfficeAgent(null);
                            applyPerson(selected as PartyOption | null);
                          }}
                          additional={{ page: 1 }}
                          defaultOptions
                          debounceTimeout={300}
                          isClearable
                          components={{ DropdownIndicator }}
                          styles={customSelectStyles}
                          menuPortalTarget={selectMenuPortal}
                        />
                      </div>
                    ) : null}
                    {selectedOffice && (selectedTeamMember || selectedOfficeAgent) ? (
                      <div className="col-lg-3 mb-3">
                        <label className="table-cells">Select Agent</label>
                        <AsyncPaginate
                          key={`office-agents-${selectedOffice.value}`}
                          classNamePrefix="rs"
                          placeholder="Select agent…"
                          value={selectedOfficeAgent}
                          loadOptions={loadOfficeAgentOptions}
                          isDisabled={!canEditCustomer}
                          cacheUniqs={[selectedOffice.value]}
                          onChange={(selected) => {
                            setSelectedOfficeAgent(selected as PartyOption | null);
                            if (selected) applyPerson(selected as PartyOption);
                            else applyPerson(selectedTeamMember);
                          }}
                          additional={{ page: 1 }}
                          defaultOptions
                          debounceTimeout={300}
                          isClearable
                          components={{ DropdownIndicator }}
                          styles={customSelectStyles}
                          menuPortalTarget={selectMenuPortal}
                        />
                      </div>
                    ) : null}
                  </>
                ) : userType === "agent" && brokerageId ? (
                  <div className="col-lg-3 mb-3">
                    <label className="table-cells">Select Agent <span className="text-danger">*</span></label>
                    <AsyncPaginate
                      key={`agent-${brokerageId}-${userType}`}
                      classNamePrefix="rs"
                      placeholder="Search agents..."
                      value={selectedAgent}
                      loadOptions={loadAgentOptions}
                      isDisabled={!canEditCustomer}
                      cacheUniqs={[brokerageId, userType]}
                      onChange={(selected) => {
                        setSelectedAgent(selected as PartyOption | null);
                        applyPerson(selected as PartyOption | null);
                      }}
                      additional={{ page: 1 }}
                      defaultOptions
                      debounceTimeout={300}
                      isClearable
                      components={{ DropdownIndicator }}
                      styles={customSelectStyles}
                      menuPortalTarget={selectMenuPortal}
                    />
                  </div>
                ) : null}

                <div className="col-lg-3 mb-3">
                  <label className="form-label table-headers">Email <span className="text-danger">*</span></label>
                  <input className="form-control table-cells" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} readOnly={!canEditCustomer} />
                </div>
                <div className="col-lg-3 mb-3">
                  <label className="form-label table-headers">Phone <span className="text-danger">*</span></label>
                  <input className="form-control table-cells" value={userPhone} onChange={(e) => setUserPhone(e.target.value)} readOnly={!canEditCustomer} />
                </div>
              </>
            )}
          </div>

          <div className="pq-create-card">
            <div className="pq-create-card-body">
              <div className="pq-form-group">
                <label className="pq-form-label">
                  Design Title <span className="text-danger">*</span>
                </label>
                <input
                  className="pq-text-input"
                  placeholder="e.g. 123 Main St Yard Sign Rider"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              {!isEdit ? (
                <div className="pq-form-group">
                  <label className="pq-form-label">
                    Upload Initial Artwork <span className="text-danger">*</span>
                  </label>
                  <div
                    className={`pq-upload-box ${artworkDragActive ? "pq-upload-box--drag" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setArtworkDragActive(true);
                    }}
                    onDragLeave={() => setArtworkDragActive(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setArtworkDragActive(false);
                      if (e.dataTransfer.files?.length) mergeArtworkFiles(e.dataTransfer.files);
                    }}
                  >
                    <i className="ri-upload-cloud-2-line pq-upload-box-icon" />
                    <div className="pq-upload-box-title">Drop files here or browse</div>
                    <div className="pq-upload-box-note">PNG, JPG, PDF, AI · at least one required</div>
                    <label className="pq-upload-box-btn">
                      Browse Files
                      <input
                        type="file"
                        multiple
                        hidden
                        accept=".png,.jpg,.jpeg,.pdf,.ai"
                        onChange={(e) => {
                          if (e.target.files?.length) mergeArtworkFiles(e.target.files);
                          e.currentTarget.value = "";
                        }}
                      />
                    </label>
                  </div>
                  {artworkFiles.length ? (
                    <div className="pq-uploaded-files">
                      {artworkFiles.map((file, idx) => (
                        <div className="pq-uploaded-file" key={`${file.name}-${idx}`}>
                          <div className="pq-uploaded-file-left">
                            <i className="ri-file-2-line pq-uploaded-file-icon" />
                            <span className="pq-uploaded-file-name">{file.name}</span>
                          </div>
                          <button
                            type="button"
                            className="pq-uploaded-file-remove"
                            onClick={() => setArtworkFiles((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            <i className="ri-close-line" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="pq-form-group">
                <label className="pq-form-label">Message to Vendor</label>
                <textarea
                  className="pq-textarea-input"
                  placeholder="Describe what you need — colors, sizes, text, headshot requirements..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>

              <div className="pq-bottom-actions mb-3">
                <button className="pq-footer-cancel-btn" type="button" onClick={() => navigate(listPath)}>
                  Cancel
                </button>
                <button className="pq-footer-primary-btn" type="button" onClick={() => void handleSubmit()} disabled={isSaving}>
                  {isSaving ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save Changes" : "Create Request"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="pq-create-right">
          <PONumber
            mode="create"
            className="mb-3"
            value={poNumber}
            onChange={setPoNumber}
          />
          <div className="pq-workflow-card">
            <div className="pq-workflow-card-head">Request Workflow</div>
            <div className="pq-workflow-card-body">
              <div className="pq-workflow-list-wrap">
                {workflowUi.map((item, index) => (
                  <div className="pq-workflow-row" key={item.label}>
                    <div className="pq-workflow-left">
                      <div className={`pq-workflow-icon ${item.tone}`}>
                        <i className={item.iconClass} />
                      </div>
                      {index !== workflowUi.length - 1 && <div className="pq-workflow-line" />}
                    </div>
                    <div className="pq-workflow-content">
                      <div className="pq-workflow-title">{item.label}</div>
                      <div className="pq-workflow-detail">{item.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignRequestAddEdit;
